import { supabase } from '../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from './imageCache';
import { mapWithConcurrency } from './concurrency';

/**
 * Max Storage operations in flight at once. Each one takes a Postgres
 * connection from the Storage API pool, so we keep this low to avoid
 * spiking the instance's connection count during batch generations.
 */
const STORAGE_CONCURRENCY = 4;

export type PreloadedImage = {
  id: number;
  fileName: string;
  signedUrl: string;
  optSignedUrl?: string;
  favorito: boolean;
  /** Nombre de la variante a la que pertenece la imagen — se usa para
   *  el título visible en GeneratedImagesInline y para el nombre del
   *  archivo al descargar. Si la imagen no tiene `variante_id` o el
   *  join no devolvió nada, queda undefined y el caller cae al fallback
   *  "Imagen" / al stem del file_name. */
  variante_nombre?: string;
};

/**
 * Preload generated images by polling for file_name availability,
 * fetching signed URLs, creating opt/ thumbnails, and warming browser cache.
 *
 * This is shared across all generation flows (AddFirstProduct, PruebaNuevaInterfaz,
 * SeleccionarProductosAnuncio, SeleccionarComposicionAnuncio, etc.)
 */
export async function preloadGeneratedImages(
  imageIds: number[],
  companyId: string
): Promise<PreloadedImage[]> {
  const maxAttempts = 10;
  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
  const pending = new Set<number>(imageIds);
  const preloadedImages: PreloadedImage[] = [];

  // Step 1: Get signed URLs for all images (with retry logic)
  for (let attempt = 1; attempt <= maxAttempts && pending.size > 0; attempt++) {
    // Process pending images with bounded concurrency per attempt
    const results = await mapWithConcurrency(
      Array.from(pending),
      STORAGE_CONCURRENCY,
      async (imageId) => {
        try {
          const { data: imageData, error: imageError } = await supabase
            .from('imagenes_generadas')
            .select(
              'file_name, favorito, variante_id, variantes:variantes(nombre)',
            )
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (imageError || !imageData?.file_name) return null;

          const filePath = `${companyId}/full/${imageData.file_name}`;

          // Reuse a still-fresh cached signed URL so repeat preloads don't
          // hit the Storage API again (one connection per request).
          let signedUrl = getCachedSignedUrl(filePath);
          if (!signedUrl) {
            const { data: signedUrlData } = await supabase.storage
              .from('generadas')
              .createSignedUrl(filePath, 3600);
            if (!signedUrlData?.signedUrl) return null;
            signedUrl = signedUrlData.signedUrl;
            setCachedSignedUrl(filePath, signedUrl);
          }

          const varianteNombre =
            (imageData as any).variante_id
              ? ((imageData as any).variantes?.nombre as string | undefined) ||
                undefined
              : undefined;

          return {
            imageId,
            fileName: imageData.file_name,
            signedUrl,
            favorito: imageData.favorito === true,
            variante_nombre: varianteNombre,
          };
        } catch (err) {
          console.error(`[preloadGeneratedImages] Error for image ${imageId}:`, err);
          return null;
        }
      },
    );

    for (const result of results) {
      if (result) {
        preloadedImages.push({
          id: result.imageId,
          fileName: result.fileName,
          signedUrl: result.signedUrl,
          favorito: result.favorito,
          variante_nombre: result.variante_nombre,
        });
        pending.delete(result.imageId);
      }
    }

    if (pending.size > 0 && attempt < maxAttempts) {
      await delay(1000);
    }
  }

  // Step 2: Create opt/ thumbnails + preload into browser cache (bounded concurrency)
  await mapWithConcurrency(preloadedImages, STORAGE_CONCURRENCY, async (img) => {
    const optPath = `${companyId}/opt/${img.fileName}`;

    // If we already have a fresh opt signed URL cached, skip the whole
    // fetch + canvas resize + re-upload. The upload is the heaviest Storage
    // operation and the main driver of connection-pool spikes, so avoiding
    // it on repeat preloads matters most.
    const cachedOpt = getCachedSignedUrl(optPath);
    if (cachedOpt) {
      img.optSignedUrl = cachedOpt;
    } else {
      // Create optimized thumbnail (300px, JPEG 80%)
      try {
        const response = await fetch(img.signedUrl);
        const blob = await response.blob();

        const optBlob = await new Promise<Blob>((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('No canvas context')); return; }

            const targetSize = 300;
            let width = image.width;
            let height = image.height;
            if (width > height) {
              if (width > targetSize) {
                height = Math.round(height * (targetSize / width));
                width = targetSize;
              }
            } else {
              if (height > targetSize) {
                width = Math.round(width * (targetSize / height));
                height = targetSize;
              }
            }
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(image, 0, 0, width, height);
            canvas.toBlob(
              (b) => (b ? resolve(b) : reject(new Error('Blob creation failed'))),
              'image/jpeg',
              0.8
            );
          };
          image.onerror = () => reject(new Error('Image load error'));
          image.src = URL.createObjectURL(blob);
        });

        // Upload opt/ version to storage
        const { error: uploadError } = await supabase.storage
          .from('generadas')
          .upload(optPath, optBlob);

        if (!uploadError) {
          // Get signed URL for the opt version
          const { data: optUrlData } = await supabase.storage
            .from('generadas')
            .createSignedUrl(optPath, 3600);
          if (optUrlData?.signedUrl) {
            img.optSignedUrl = optUrlData.signedUrl;
            setCachedSignedUrl(optPath, optUrlData.signedUrl);
          }
        }
      } catch (err) {
        // Non-critical: opt creation failed, full URL still works
        console.warn(`[preloadGeneratedImages] opt creation failed for ${img.fileName}:`, err);
      }
    }

    // Preload the smaller opt URL (or full as fallback) into browser cache
    await new Promise<void>((resolve) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => resolve();
      image.src = img.optSignedUrl || img.signedUrl;
    });
  });

  return preloadedImages;
}
